import { max } from "date-fns";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as SparepartAssemblyActions from "./actions";
import type { ISparepartAssemblyPositions, ISparepartAssemblyReducerState } from "./types";

export type SparepartAssemblyAction = ActionType<typeof SparepartAssemblyActions>;

// use for initialisation and reset
const resetModalState = {
  isModalOpen: false,
  modalPositionId: null! as string,
  modalComment: null! as string,
  modalEarliestDate: null! as Date,
  modalRequestedDate: null! as Date
};

const initialAssemblyDatesState: ISparepartAssemblyReducerState = {
  ...resetModalState,
  positions: {}
};

export default function sparepartAssemblyReducer(
  state: ISparepartAssemblyReducerState = initialAssemblyDatesState,
  action: SparepartAssemblyAction
): ISparepartAssemblyReducerState {
  switch (action.type) {
    // open the modal and restore data from positions otherwise with init values
    case getType(SparepartAssemblyActions.openSparepartAssemblyModal): {
      // action payload is the initial data
      const { id, ...initialData } = action.payload;

      const restoredValue = state.positions[id];

      // we prefer the current data to the initial data

      const modalComment = restoredValue?.comment ?? initialData.comment ?? "";

      // also make sure that the date is not less than today
      const modalEarliestDate = max([restoredValue?.earliestDate ?? initialData.earliestDate, new Date()]);
      const modalRequestedDate = max([restoredValue?.requestedDate ?? initialData.requestedDate, modalEarliestDate]);

      return {
        ...state,
        isModalOpen: true,
        modalPositionId: id,
        modalComment,
        modalEarliestDate,
        modalRequestedDate
      };
    }

    case getType(SparepartAssemblyActions.setSparepartAssemblyPositionInfo): {
      const { id, ...initialData } = action.payload;
      const { comment, earliestDate, requestedDate } = initialData;
      return {
        ...state,
        positions: { ...state.positions, [action.payload.id]: { comment, earliestDate, requestedDate, isEnabled: true } }
      };
    }

    // close and reset input values
    case getType(SparepartAssemblyActions.closeSparepartAssemblyModal):
      return { ...state, ...resetModalState };

    // close and save
    case getType(SparepartAssemblyActions.closeAndSaveSparepartAssemblyModal): {
      const { comment, earliestDate, requestedDate } = action.payload;
      return {
        ...state,
        ...resetModalState,
        positions: { ...state.positions, [action.payload.id]: { comment, earliestDate, requestedDate, isEnabled: true } }
      };
    }

    // prevent an existing item info from being passed to next checkout step
    case getType(SparepartAssemblyActions.disableSparepartAssemblyPositionInfo):
      return {
        ...state,
        positions: { ...state.positions, [action.payload.id]: { ...state.positions[action.payload.id], isEnabled: false } } as ISparepartAssemblyPositions
      };

    // clear the date after successful orderSubmit
    case getType(SparepartAssemblyActions.clearSparepartAssemblyData):
      return { ...state, ...resetModalState, positions: {} };

    case getType(SparepartAssemblyActions.clearSparepartAssemblyPositionInfo): {
      const id = action.payload.id;
      const { [id]: posData, ...positions } = state.positions;
      return { ...state, positions };
    }

    default:
      return state;
  }
}
